import { memo } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { useGlobalAudioPlayback } from '@/audio/GlobalAudioContext';
import { useGlobalAudioScrub } from '@/audio/useGlobalAudioScrub';
import { formatAudioClock } from '@/components/ProjectFilesPopover/projectFilesPopoverUtils';
import { cn } from './cn';

export type FonotekaAudioPlayerProps = {
  instanceId: string;
  src: string;
  labelForA11y: string;
  packId?: string;
  disabled?: boolean;
};

function PlayIcon() {
  return (
    <svg width="11" height="11" viewBox="0 0 12 12" fill="currentColor" aria-hidden>
      <path d="M3 1.6v8.8L11 6 3 1.6z" />
    </svg>
  );
}

function PauseIcon() {
  return (
    <svg width="11" height="11" viewBox="0 0 12 12" fill="currentColor" aria-hidden>
      <path d="M2 1.2h2.4v9.6H2V1.2zm5.6 0H10v9.6H7.6V1.2z" />
    </svg>
  );
}

function IdleTimeline({ labelForA11y }: { labelForA11y: string }) {
  return (
    <>
      <span className="fonoteka-time" aria-hidden>
        {formatAudioClock(0)}
      </span>
      <div
        className="fonoteka-bar"
        role="slider"
        tabIndex={-1}
        aria-label={`Позиция воспроизведения: ${labelForA11y}`}
        aria-valuemin={0}
        aria-valuemax={1000}
        aria-valuenow={0}
        aria-valuetext={`${formatAudioClock(0)} из ${formatAudioClock(0)}`}
        aria-disabled
      >
        <i style={{ width: '0%' }} />
      </div>
      <span className="fonoteka-time" aria-hidden>
        {formatAudioClock(0)}
      </span>
    </>
  );
}

function LiveTimeline({ labelForA11y }: { labelForA11y: string }) {
  const scrub = useGlobalAudioScrub();
  return (
    <>
      <span className="fonoteka-time" aria-hidden>
        {formatAudioClock(scrub.currentTimeSec)}
      </span>
      <div
        ref={scrub.barRef}
        className="fonoteka-bar"
        role="slider"
        tabIndex={0}
        aria-label={`Позиция воспроизведения: ${labelForA11y}`}
        aria-valuemin={0}
        aria-valuemax={scrub.ariaValueMax}
        aria-valuenow={scrub.ariaValueNow}
        aria-valuetext={`${formatAudioClock(scrub.currentTimeSec)} из ${formatAudioClock(scrub.durationSec)}`}
        aria-disabled={!scrub.durationKnown}
        onPointerDown={scrub.onTrackPointerDown}
        onPointerMove={scrub.onTrackPointerMove}
        onPointerUp={scrub.onTrackPointerUp}
        onPointerCancel={scrub.onTrackPointerCancel}
        onKeyDown={scrub.onTrackKeyDown}
      >
        <i style={{ width: `${scrub.progressRatio * 100}%` }} />
      </div>
      <span className="fonoteka-time" aria-hidden>
        {formatAudioClock(scrub.durationSec)}
      </span>
    </>
  );
}

export const FonotekaAudioPlayer = memo(function FonotekaAudioPlayer({
  instanceId,
  src,
  labelForA11y,
  packId,
  disabled = false,
}: FonotekaAudioPlayerProps) {
  const { track, isPlaying, play } = useGlobalAudioPlayback();
  const isCurrent = track?.instanceId === instanceId;
  const showPlaying = isCurrent && isPlaying;

  if (!src.trim()) return null;

  const iconKey = showPlaying ? 'pause' : 'play';

  return (
    <div
      className={cn('fonoteka-player', disabled && 'opacity-50')}
      onClick={(e) => e.stopPropagation()}
      onPointerDown={(e) => e.stopPropagation()}
    >
      <button
        type="button"
        className="fonoteka-play"
        aria-label={showPlaying ? `Пауза: ${labelForA11y}` : `Воспроизвести: ${labelForA11y}`}
        disabled={disabled}
        onClick={(e) => {
          e.stopPropagation();
          if (disabled) return;
          play({
            instanceId,
            src,
            title: labelForA11y,
            source: 'fonoteka',
            packId,
          });
        }}
      >
        <AnimatePresence mode="wait" initial={false}>
          <motion.span
            key={iconKey}
            className="inline-flex items-center justify-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
          >
            {showPlaying ? <PauseIcon /> : <PlayIcon />}
          </motion.span>
        </AnimatePresence>
      </button>
      {isCurrent && !disabled ? (
        <LiveTimeline labelForA11y={labelForA11y} />
      ) : (
        <IdleTimeline labelForA11y={labelForA11y} />
      )}
    </div>
  );
});
